// Pre-encryption / post-decryption gate for attachment payloads.
//
// The server never sees plaintext, so the server cannot be the scanner —
// every policy decision must run on the client. This module enforces a
// closed-set type allow-list, per-category byte cap, and magic-byte sniff
// against MIME spoofing. Decisions are pure functions of (claimed MIME,
// byte size, first bytes) so they are exhaustively testable without I/O.
//
// Decision ordering inside `decide`:
//   1. byteSize > max(category)  → reject "size_exceeded"
//   2. magic-byte mismatch       → reject "mime_spoof"
// Size is checked first so a 10GB blob masquerading as a PNG is rejected
// without ever materialising the bytes for the magic sniff.
//
// The type allow-list is encoded as the `AttachmentMimeType` union: anything
// not listed is unrepresentable at the call site, so "unknown MIME" cannot
// reach `decide` at all. Callers that receive an arbitrary MIME string
// from disk / pasteboard must map it through `mimeTypeFromString` and treat
// `null` as a reject themselves (same as "not in allow-list").
//
// Web port of `kaether_for_ios/Stoat/E2EE/AttachmentPolicy.swift`.

export type AttachmentCategory =
  | "image"
  | "video"
  | "audio"
  | "voice"
  | "pdf"
  | "generic";

export type AttachmentMimeType =
  | "image/jpeg"
  | "image/png"
  | "image/webp"
  | "image/gif"
  | "video/mp4"
  | "video/quicktime"
  | "audio/mp4"
  | "audio/aac"
  | "audio/opus"
  | "application/pdf"
  | "text/plain"
  | "application/octet-stream";

/** Closed set of allowed MIME type strings for runtime parsing. */
const ALLOWED_MIME_TYPES = new Set<string>([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/quicktime",
  "audio/mp4",
  "audio/aac",
  "audio/opus",
  "application/pdf",
  "text/plain",
  "application/octet-stream",
]);

/**
 * Parse an arbitrary MIME string into the closed-set enum.
 * Returns `null` if the string is not in the allow-list.
 * Callers must treat `null` as a reject before calling `decide`.
 */
export function mimeTypeFromString(s: string): AttachmentMimeType | null {
  if (ALLOWED_MIME_TYPES.has(s)) return s as AttachmentMimeType;
  return null;
}

/**
 * Map a MIME type onto a coarse category used to look up the size cap.
 * `audio/opus` is special-cased to `voice` because voice memos have a
 * tighter cap than music attachments.
 */
export function categoryForMimeType(mime: AttachmentMimeType): AttachmentCategory {
  switch (mime) {
    case "image/jpeg":
    case "image/png":
    case "image/webp":
    case "image/gif":
      return "image";
    case "video/mp4":
    case "video/quicktime":
      return "video";
    case "audio/opus":
      return "voice";
    case "audio/mp4":
    case "audio/aac":
      return "audio";
    case "application/pdf":
      return "pdf";
    case "text/plain":
    case "application/octet-stream":
      return "generic";
  }
}

// ---------------------------------------------------------------------------
// Size budget
// Byte caps per category. Values are intentionally conservative — they
// bound the cost of a malicious peer trying to flood storage / bandwidth.
// These are byte-for-byte mirrors of `AttachmentSizeBudget` in the iOS port.
// ---------------------------------------------------------------------------

export const AttachmentSizeBudget = {
  imageMax: 25 * 1024 * 1024,
  videoMax: 250 * 1024 * 1024,
  audioMax: 50 * 1024 * 1024,
  voiceMax: 3 * 1024 * 1024,
  pdfMax: 100 * 1024 * 1024,
  genericMax: 25 * 1024 * 1024,

  maxBytes(category: AttachmentCategory): number {
    switch (category) {
      case "image":   return AttachmentSizeBudget.imageMax;
      case "video":   return AttachmentSizeBudget.videoMax;
      case "audio":   return AttachmentSizeBudget.audioMax;
      case "voice":   return AttachmentSizeBudget.voiceMax;
      case "pdf":     return AttachmentSizeBudget.pdfMax;
      case "generic": return AttachmentSizeBudget.genericMax;
    }
  },
} as const;

// ---------------------------------------------------------------------------
// Policy decision
// ---------------------------------------------------------------------------

/** Audit record explaining why the policy rejected an attachment. */
export interface AttachmentRejection {
  /** Human-readable reason safe to surface in UI. */
  readonly reason: string;
  /** Stable short code for analytics correlation. */
  readonly code: string;
}

export type AttachmentPolicyDecision =
  | { readonly kind: "allow" }
  | { readonly kind: "reject"; readonly rejection: AttachmentRejection };

export const allow: AttachmentPolicyDecision = { kind: "allow" };

function reject(reason: string, code: string): AttachmentPolicyDecision {
  return { kind: "reject", rejection: { reason, code } };
}

/**
 * Pure-function gate. Runs on outbound (before E2EE encrypt) AND on
 * inbound (after E2EE decrypt) so that neither a malicious sender nor
 * a malicious server can smuggle disallowed content past the policy.
 *
 * `firstBytes` is the first up-to-16 bytes of the plaintext payload;
 * callers should pass exactly what they have, the magic-byte tables
 * do not read past index 11.
 *
 * Decision order (mirrors iOS — this ordering is a security choice):
 *   1. byteSize > max(category)  → reject "size_exceeded"
 *   2. magic-byte mismatch       → reject "mime_spoof"
 *   3. else                      → allow
 */
export function decide(
  claimedMimeType: AttachmentMimeType,
  byteSize: number,
  firstBytes: Uint8Array,
): AttachmentPolicyDecision {
  // 1. Size is checked first so an over-cap blob is rejected without
  //    having to materialise its bytes for the magic sniff.
  const category = categoryForMimeType(claimedMimeType);
  const cap = AttachmentSizeBudget.maxBytes(category);
  if (byteSize > cap) {
    return reject(
      `attachment exceeds size cap (${byteSize} > ${cap} bytes for ${claimedMimeType})`,
      "size_exceeded",
    );
  }

  // 2. Magic-byte sniff — check that the claimed MIME matches the actual
  //    file header. Types without a stable single-format signature skip
  //    the check and pass through.
  if (!magicBytesMatch(claimedMimeType, firstBytes)) {
    return reject(
      `declared MIME ${claimedMimeType} does not match file magic bytes`,
      "mime_spoof",
    );
  }

  return allow;
}

// ---------------------------------------------------------------------------
// Magic-byte helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if `firstBytes` is consistent with what the claimed MIME
 * would produce. Types without a stable single-format signature (opus, aac,
 * octet-stream, text/plain) skip the check and return true — those formats
 * have multiple legitimate container shapes and a magic-byte false positive
 * would block real content.
 */
function magicBytesMatch(
  claimedMimeType: AttachmentMimeType,
  firstBytes: Uint8Array,
): boolean {
  switch (claimedMimeType) {
    case "image/png":
      // 8-byte PNG signature
      return startsWith(firstBytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    case "image/jpeg":
      // 3-byte JPEG SOI marker
      return startsWith(firstBytes, [0xff, 0xd8, 0xff]);
    case "image/gif":
      // GIF87a or GIF89a (ASCII)
      return startsWithAscii(firstBytes, "GIF87a") || startsWithAscii(firstBytes, "GIF89a");
    case "image/webp":
      // RIFF at offset 0, WEBP at offset 8
      return hasAsciiAt(firstBytes, "RIFF", 0) && hasAsciiAt(firstBytes, "WEBP", 8);
    case "application/pdf":
      // %PDF- (ASCII)
      return startsWithAscii(firstBytes, "%PDF-");
    case "video/mp4":
    case "video/quicktime":
      // ISO BMFF: the `ftyp` box marker lives at bytes 4..7. We don't
      // try to discriminate between mp4 and mov beyond that — both are
      // ftyp-prefixed and both go through the same decode path.
      return hasAsciiAt(firstBytes, "ftyp", 4);
    case "audio/opus":
    case "audio/aac":
    case "audio/mp4":
    case "text/plain":
    case "application/octet-stream":
      // No magic-byte enforcement: legitimate variants of these formats
      // use multiple container shapes (ADTS vs MP4 for AAC, raw vs Ogg
      // for Opus, UTF-8 vs UTF-16-BOM for text/plain) and a strict check
      // would reject real content.
      return true;
  }
}

function startsWith(data: Uint8Array, prefix: readonly number[]): boolean {
  if (data.length < prefix.length) return false;
  for (let i = 0; i < prefix.length; i++) {
    if (data[i] !== prefix[i]) return false;
  }
  return true;
}

function startsWithAscii(data: Uint8Array, ascii: string): boolean {
  const bytes: number[] = [];
  for (let i = 0; i < ascii.length; i++) bytes.push(ascii.charCodeAt(i));
  return startsWith(data, bytes);
}

function hasAsciiAt(data: Uint8Array, ascii: string, offset: number): boolean {
  const bytes: number[] = [];
  for (let i = 0; i < ascii.length; i++) bytes.push(ascii.charCodeAt(i));
  if (data.length < offset + bytes.length) return false;
  for (let i = 0; i < bytes.length; i++) {
    if (data[offset + i] !== bytes[i]) return false;
  }
  return true;
}
